import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, FlaskConical, Loader2, Search } from "lucide-react";
import {
  detectCountryIsoFromE164,
  normalizeContactPhoneE164,
  type CampaignRecipientListItem,
} from "@multi-chat/shared";
import { listCampaignRecipients } from "../../api/campaigns-api.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { InternationalPhoneField } from "../phone/InternationalPhoneField.js";
import {
  campaignAudienceActionBaseClass,
  campaignAudiencePrimaryActionClass,
} from "./CampaignManageRecipientsButton.js";
import { CampaignModalContent } from "./CampaignModalContent.js";
import { CAMPAIGN_DEFAULT_COUNTRY_ISO, recipientDisplayName } from "./campaigns-presentation.js";

const PAGE_LIMIT = 100;

function recipientMatches(item: CampaignRecipientListItem, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.length === 0) return true;
  const name = recipientDisplayName(item).toLowerCase();
  const phone = item.normalizedPhoneE164.toLowerCase().replace(/\s+/g, "");
  return name.includes(trimmed) || phone.includes(trimmed.replace(/\s+/g, ""));
}

export function CampaignSendTestDialog({
  open,
  organizationId,
  campaignId,
  sending,
  errorMessage,
  onOpenChange,
  onSend,
}: {
  open: boolean;
  organizationId: number;
  campaignId: number;
  sending: boolean;
  errorMessage: string | null;
  onOpenChange: (open: boolean) => void;
  onSend: (input: {
    sampleRecipientId: string;
    destinationPhone: string;
    defaultCountryIso: string;
  }) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [loadedRecipients, setLoadedRecipients] = useState<CampaignRecipientListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [sampleRecipientId, setSampleRecipientId] = useState<string | null>(null);
  const [destinationPhone, setDestinationPhone] = useState("");
  const [defaultCountryIso, setDefaultCountryIso] = useState(CAMPAIGN_DEFAULT_COUNTRY_ISO);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoadedRecipients([]);
    setNextCursor(null);
    setSampleRecipientId(null);
    setDestinationPhone("");
    setDefaultCountryIso(CAMPAIGN_DEFAULT_COUNTRY_ISO);
    setQuery("");
    void listCampaignRecipients(
      organizationId,
      campaignId,
      { limit: PAGE_LIMIT },
      controller.signal,
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        setLoadedRecipients(response.items);
        setNextCursor(response.nextCursor);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setLoadedRecipients([]);
        setNextCursor(null);
      });
    return () => controller.abort();
  }, [campaignId, open, organizationId]);

  const selected =
    sampleRecipientId != null
      ? (loadedRecipients.find((item) => item.id === sampleRecipientId) ?? null)
      : null;
  const filtered = useMemo(
    () => loadedRecipients.filter((item) => recipientMatches(item, query)),
    [loadedRecipients, query],
  );
  const destinationNormalized = normalizeContactPhoneE164({
    raw: destinationPhone,
    defaultCountryIso,
  });
  // The send-test API requires a sample for personalization; fall back to the first recipient.
  const fallbackRecipient = loadedRecipients[0] ?? null;
  const effectiveSampleId = sampleRecipientId ?? fallbackRecipient?.id ?? null;
  const canSend = effectiveSampleId != null && destinationNormalized.ok && !sending;

  const chooseRecipient = (item: CampaignRecipientListItem) => {
    setSampleRecipientId(item.id);
    setDestinationPhone(item.normalizedPhoneE164);
    const iso = detectCountryIsoFromE164(item.normalizedPhoneE164);
    if (iso) setDefaultCountryIso(iso);
    setPickerOpen(false);
    setQuery("");
  };

  const loadMore = () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    void listCampaignRecipients(organizationId, campaignId, {
      cursor: nextCursor,
      limit: PAGE_LIMIT,
    })
      .then((response) => {
        setLoadedRecipients((current) => {
          const seen = new Set(current.map((item) => item.id));
          const next = current.slice();
          for (const item of response.items) {
            if (seen.has(item.id)) continue;
            seen.add(item.id);
            next.push(item);
          }
          return next;
        });
        setNextCursor(response.nextCursor);
      })
      .finally(() => setLoadingMore(false));
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent data-testid="campaign-send-test-dialog">
        <div className={contactsModalHeaderClass}>
          <AlertDialogTitle className="flex items-center gap-2.5">
            <span
              className="grid size-8 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-foreground"
              aria-hidden="true"
            >
              <FlaskConical size={16} />
            </span>
            {t("campaigns.review.sendTest")}
          </AlertDialogTitle>
          <AlertDialogDescription>{t("campaigns.review.sendTestHint")}</AlertDialogDescription>
        </div>
        <div className={cn(contactsModalBodyClass, "grid gap-4")}>
          <div className="grid gap-1.5">
            <p className="m-0 text-[0.75rem] font-medium">{t("campaigns.review.sampleRecipient")}</p>
            <Popover modal open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-start text-[0.8125rem] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]"
                  data-testid="campaign-send-test-recipient"
                >
                  {selected ? (
                    <span className="grid min-w-0 flex-1 justify-items-start gap-0.5">
                      <bdi className="max-w-full truncate font-semibold text-foreground">
                        {recipientDisplayName(selected)}
                      </bdi>
                      <LtrValue className="max-w-full truncate text-[0.75rem] tabular-nums text-muted">
                        {selected.normalizedPhoneE164}
                      </LtrValue>
                    </span>
                  ) : (
                    <span className="text-muted">{t("campaigns.review.chooseSampleRecipient")}</span>
                  )}
                  <ChevronDown size={14} className="shrink-0 opacity-60" aria-hidden="true" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0">
                <div className="flex items-center gap-2 border-b border-line px-2.5 py-2">
                  <Search size={14} className="shrink-0 text-muted" aria-hidden="true" />
                  <input
                    className="min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setHighlight(0);
                    }}
                    placeholder={t("campaigns.review.searchRecipients")}
                    data-testid="campaign-send-test-recipient-search"
                    onKeyDown={(event) => {
                      if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setHighlight((current) =>
                          Math.min(current + 1, Math.max(filtered.length - 1, 0)),
                        );
                      } else if (event.key === "ArrowUp") {
                        event.preventDefault();
                        setHighlight((current) => Math.max(current - 1, 0));
                      } else if (event.key === "Enter") {
                        event.preventDefault();
                        const item = filtered[highlight];
                        if (item) chooseRecipient(item);
                      } else if (event.key === "Escape") {
                        event.preventDefault();
                        setPickerOpen(false);
                      }
                    }}
                  />
                </div>
                <div role="listbox" className="max-h-64 overflow-y-auto p-1">
                  {filtered.length === 0 ? (
                    <p className="m-0 px-2 py-3 text-[0.8125rem] text-muted">
                      {t("campaigns.review.noRecipientMatches")}
                    </p>
                  ) : (
                    filtered.map((item, index) => (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={item.id === sampleRecipientId}
                        className={cn(
                          "grid w-full justify-items-start gap-0.5 rounded-md px-2 py-1.5 text-start hover:bg-surface-muted",
                          index === highlight ? "bg-surface-muted" : null,
                        )}
                        onMouseEnter={() => setHighlight(index)}
                        onClick={() => chooseRecipient(item)}
                      >
                        <bdi className="max-w-full truncate text-[0.8125rem] font-medium">
                          {recipientDisplayName(item)}
                        </bdi>
                        <LtrValue className="max-w-full truncate text-[0.75rem] tabular-nums text-muted">
                          {item.normalizedPhoneE164}
                        </LtrValue>
                      </button>
                    ))
                  )}
                  {query.trim().length > 0 && nextCursor ? (
                    <p className="m-0 px-2 py-2 text-[0.75rem] text-muted" data-testid="campaign-send-test-search-more">
                      {t("campaigns.review.searchMayHaveMore")}
                    </p>
                  ) : null}
                  {nextCursor ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="m-1"
                      disabled={loadingMore}
                      onClick={loadMore}
                      data-testid="campaign-send-test-load-more"
                    >
                      {loadingMore ? (
                        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                      ) : null}
                      {t("campaigns.review.loadMoreRecipients")}
                    </Button>
                  ) : null}
                </div>
              </PopoverContent>
            </Popover>
            {selected == null && fallbackRecipient ? (
              <p className="m-0 text-[0.75rem] text-muted" data-testid="campaign-send-test-sample-fallback">
                {t("campaigns.review.sampleRecipientFallback", {
                  name: recipientDisplayName(fallbackRecipient),
                })}
              </p>
            ) : null}
          </div>

          <InternationalPhoneField
            id="campaign-send-test-phone"
            label={t("campaigns.review.sendTestTo")}
            value={destinationPhone}
            onChange={setDestinationPhone}
            onCountryIsoChange={setDefaultCountryIso}
            defaultCountryIso={defaultCountryIso}
            numberTestId="campaign-send-test-phone"
            data-testid="campaign-send-test-destination"
          />
          {errorMessage ? (
            <p className="m-0 text-[0.8125rem] text-error" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>
        <AlertDialogActions className={contactsModalFooterClass}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={campaignAudienceActionBaseClass}
            onClick={() => onOpenChange(false)}
          >
            {t("campaigns.actions.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            className={cn(campaignAudiencePrimaryActionClass, "disabled:cursor-not-allowed")}
            disabled={!canSend}
            data-testid="campaign-send-test-submit"
            onClick={() => {
              if (effectiveSampleId == null || !destinationNormalized.ok) return;
              onSend({
                sampleRecipientId: effectiveSampleId,
                destinationPhone,
                defaultCountryIso,
              });
            }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}
            {sending ? t("campaigns.review.sending") : t("campaigns.review.sendTest")}
          </Button>
        </AlertDialogActions>
      </CampaignModalContent>
    </AlertDialog>
  );
}
